/*======================================================================================================================
  * Bluebell: src/app/state/annotations/annotations.reducer.ts
  * Last Edited: 16/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { deselectAllResults } from '@state/results/results.actions';
import {
  clearCasesWithAnnotations,
  deleteAnnotationSuccess,
  loadAnnotations,
  loadAnnotationsFailure,
  loadAnnotationsSuccess,
  listCasesWithAnnotations,
  listCasesWithAnnotationsFailure,
  listCasesWithAnnotationsSuccess, reselectLoadedAnnotations,
  setAnnotationApprovalSuccess,
  updateAnnotationDescriptionSuccess,
  updateAnnotationOrderSuccess, annotationThumbnailClick,
  setAllowMultipleAnnotations,
} from '@state/annotations/annotations.actions';
import { Annotation, CaseWithAnnotation } from '@state/annotations/annotations.interfaces';
import { deselectPostproVariable, postproViewerClosed, selectPostproVariable } from '@state/postpro/postpro.actions';


export interface State extends EntityState<Annotation> {
  loading: boolean;
  casesWithAnnotations: CaseWithAnnotation[];
  casesWithAnnotationsLoading: boolean;
  selectedCases: string[];
  selectedAnnotations: string[];
  loadedCases: string[];
  allowMultiple: boolean;
}

export const adapter = createEntityAdapter<Annotation>({
  selectId: (annotation: Annotation) => annotation.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  loading: false,
  casesWithAnnotations: [],
  casesWithAnnotationsLoading: false,
  selectedCases: [],
  selectedAnnotations: [],
  loadedCases: [],
  allowMultiple: false,
});

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return initialState;
  }),

  // List Annotations in full

  on(loadAnnotations, (state) => {
    return { ...state, loading: true };
  }),

  on(loadAnnotationsSuccess, (state, { annotations, uuid }) => {
    return adapter.upsertMany(
      annotations,
      {
        ...state,
        selectedCases: [...new Set([...state.selectedCases, uuid])],
        loadedCases: [...new Set([...state.loadedCases, uuid])],
        loading: false,
        selectedAnnotations: state.selectedAnnotations.length
          ? state.selectedAnnotations
          : annotations.length
            ? [annotations[0].uuid]
            : [],
      }
    );
  }),

  on(loadAnnotationsFailure, (state) => {
    return {
      ...state,
      loading: false
    };
  }),


  // List Cases with Annotations

  on(listCasesWithAnnotations, (state) => {
    return { ...state, casesWithAnnotationsLoading: true };
  }),
  on(listCasesWithAnnotationsSuccess, (state, { casesWithAnnotations }) => {
    return { ...state, casesWithAnnotations, casesWithAnnotationsLoading: false };
  }),
  on(listCasesWithAnnotationsFailure, (state) => {
    return { ...state, casesWithAnnotations: [], casesWithAnnotationsLoading: false };
  }),

  // Misc

  on(
    updateAnnotationDescriptionSuccess,
    setAnnotationApprovalSuccess,
    (state, { annotation }) => {
      return adapter.upsertOne(annotation, state);
    }
  ),

  on(deleteAnnotationSuccess, (state, { uuid }) => {
    return adapter.removeOne(uuid, state);
  }),

  on(updateAnnotationOrderSuccess, (state, { annotations }) => {
    return adapter.setAll(annotations, state);
  }),

  // Results

  on(
    clearCasesWithAnnotations,
    deselectAllResults,
    (state) => {
    return adapter.removeAll({
      ...state,
      casesWithAnnotations: [],
    });
  }),

  /**
   * Component Actions
   */

  on(selectPostproVariable, (state, { postproType }) => {
    if (postproType === 'annotation') {
      return state;
    } else {
      return adapter.removeAll({
        ...state,
        selectedCases: [],
        loadedCases: [],
      });
    }
  }),

  on(deselectPostproVariable, (state, { variable, postproType }) => {
    if (postproType === 'annotation') {
      return {
        ...state,
        selectedCases: state.selectedCases.filter(selected => selected !== variable)
      };
    } else {
      return state;
    }
  }),

  on(reselectLoadedAnnotations, (state, { uuid }) => {
    return {
      ...state,
      selectedCases: [...new Set([...state.selectedCases, uuid])],
    };
  }),

  on(annotationThumbnailClick, (state, { uuid }) => {
    if (state.allowMultiple) {
      const annotationSet = new Set(state.selectedAnnotations);
      if (annotationSet.has(uuid)) {
        annotationSet.delete(uuid);
      } else {
        annotationSet.add(uuid);
      }
      return {
        ...state,
        selectedAnnotations: [...annotationSet],
      };
    } else {
      return {
        ...state,
        selectedAnnotations: [uuid],
      };
    }
  }),

  on(setAllowMultipleAnnotations, (state, { allowMultiple }) => {
    return {
      ...state,
      allowMultiple,
      selectedAnnotations: allowMultiple ? state.selectedAnnotations : [state.selectedAnnotations[0]]
    };
  }),

  on(postproViewerClosed, (state) => {
    return adapter.removeAll({
      ...state,
      loading: false,
      selectedCases: [],
      selectedAnnotations: [],
      loadedCases: [],
      allowMultiple: false,
    });
  }),

  on(deselectPostproVariable, (state, { variable, postproType }) => {
    if (postproType === 'annotation') {
      return {
        ...state,
        selectedAnnotations: state.selectedAnnotations.filter(
          annotation => variable !== state.entities[annotation]?.case.uuid
        ),
      };
    } else {
      return state;
    }
  }),





);

export const {
  selectIds: getIds,
  selectEntities: getEntities,
  selectTotal: getTotal,
  selectAll: getAll,
} = adapter.getSelectors();
export const featureKey = 'annotations';
export const getLoading = (state: State) => state.loading;
export const getCasesWithAnnotations = (state: State) => state.casesWithAnnotations;
export const getCasesWithAnnotationsLoading = (state: State) => state.casesWithAnnotationsLoading;
export const getSelectedCases = (state: State) => state.selectedCases;
export const getSelectedAnnotations = (state: State) => state.selectedAnnotations;
export const getLoadedCases = (state: State) => state.loadedCases;
export const getAllowMultiple = (state: State) => state.allowMultiple;
